import * as React from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import type { DateRange as DayPickerRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toISODate, useDateFormat } from "@/lib/dates";

function fromISO(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

export interface DatePickerProps {
  /** ISO yyyy-MM-dd value ("" / undefined = empty). */
  value?: string | null;
  /** Receives ISO yyyy-MM-dd, or "" when cleared. */
  onChange: (iso: string) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  /** ISO bounds (inclusive). */
  minDate?: string;
  maxDate?: string;
  id?: string;
  className?: string;
  align?: "start" | "center" | "end";
}

/**
 * The app-wide date picker: button trigger showing the date in the user's
 * preferred format, a calendar with month/year dropdowns, and a text field
 * that accepts typed dates. Values are always ISO yyyy-MM-dd strings, so it
 * is a drop-in replacement for `<Input type="date">`.
 */
export function DatePicker({
  value,
  onChange,
  placeholder,
  disabled,
  clearable = true,
  minDate,
  maxDate,
  id,
  className,
  align = "start",
}: DatePickerProps) {
  const { format, parse, placeholder: fmtPlaceholder } = useDateFormat();
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selected = fromISO(value);
  const displayValue = selected ? format(selected) : "";

  // Commit whatever was typed. Only Enter closes the popover — the input
  // also blurs when clicking the calendar's month/year dropdowns, and
  // closing on blur made those unusable.
  const commitText = (close: boolean) => {
    const text = inputRef.current?.value ?? "";
    if (!text.trim()) {
      if (clearable) onChange("");
      return;
    }
    const parsed = parse(text);
    if (parsed) {
      onChange(toISODate(parsed));
      if (close) setOpen(false);
    } else if (inputRef.current) {
      inputRef.current.value = displayValue;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">
            {selected ? format(selected) : (placeholder ?? fmtPlaceholder)}
          </span>
          {clearable && selected && !disabled && (
            <X
              className="ml-1 h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <div className="border-b p-2">
          <Input
            key={`${value ?? ""}-${open}`}
            ref={inputRef}
            defaultValue={displayValue}
            placeholder={fmtPlaceholder}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitText(true);
              }
            }}
            onBlur={() => commitText(false)}
            className="h-8"
          />
        </div>
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={selected}
          defaultMonth={selected}
          disabled={
            minDate || maxDate
              ? [
                  ...(minDate ? [{ before: fromISO(minDate)! }] : []),
                  ...(maxDate ? [{ after: fromISO(maxDate)! }] : []),
                ]
              : undefined
          }
          onSelect={(d) => {
            onChange(d ? toISODate(d) : "");
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export interface DateRangeValue {
  start: string; // ISO or ""
  end: string; // ISO or ""
}

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  id?: string;
  className?: string;
  align?: "start" | "center" | "end";
}

/** Range variant: one popover, two-month calendar, same ISO string API. */
export function DateRangePicker({
  value,
  onChange,
  placeholder = "Select period",
  disabled,
  clearable = true,
  id,
  className,
  align = "start",
}: DateRangePickerProps) {
  const { format } = useDateFormat();
  const [open, setOpen] = React.useState(false);
  const from = fromISO(value.start);
  const to = fromISO(value.end);

  const label =
    from && to
      ? `${format(from)} – ${format(to)}`
      : from
        ? `${format(from)} – …`
        : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal",
            !from && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">{label}</span>
          {clearable && (from || to) && !disabled && (
            <X
              className="ml-1 h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onChange({ start: "", end: "" });
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="range"
          numberOfMonths={2}
          captionLayout="dropdown"
          selected={{ from, to } as DayPickerRange}
          defaultMonth={from}
          onSelect={(range: DayPickerRange | undefined) => {
            onChange({
              start: range?.from ? toISODate(range.from) : "",
              end: range?.to ? toISODate(range.to) : "",
            });
            if (range?.from && range?.to) setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
